import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass, faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import { buildUrl, fetchJson } from "../shared/api";
import { isVersionHigher, isReleaseCandidate } from "../shared/version";
import type { ConnectorInfo, Version } from "../shared/types/api";
import { useTranslation } from "react-i18next";

type RepoMap = Record<string, ReturnType<typeof createEmptyRepo>>;

function createEmptyRepo(connector: ConnectorInfo) {
    return {
        ...connector,
        uniqueKey: `${connector.nameOfRepo}-${connector.tableName}`,
        version: "",
        versions: [] as Version[],
    };
}

function ConnectorsPage() {
    const { t } = useTranslation();

    const [repoData, setRepoData] = useState<RepoMap>({});
    const [nameFilter, setNameFilter] = useState<string>("");

    useEffect(() => {
        let cancelled = false;

        const fetchData = async () => {
            try {
                const [connectors, versions] = await Promise.all([
                    fetchJson<ConnectorInfo[]>(buildUrl("/connector/all")),
                    fetchJson<Version[]>(buildUrl("/version/all")),
                ]);

                if (cancelled) return;

                const map: RepoMap = {};

                for (const con of connectors) {
                    if (!con.nameOfRepo) continue;
                    const key = `${con.nameOfRepo}-${con.tableName}`;
                    map[key] = createEmptyRepo(con);
                }

                const versionsByRepo: Record<string, Version[]> = {};
                for (const ver of versions) {
                    if (!ver.repository || !ver.tag || ver.tag.toLowerCase().includes("snapshot") || isReleaseCandidate(ver.tag)) continue;
                    (versionsByRepo[ver.repository] ??= []).push(ver);
                }

                for (const key in map) {
                    const repo = map[key];
                    const repoVersions = versionsByRepo[repo.nameOfRepo] || [];

                    for (const ver of repoVersions) {
                        if (!repo.version || isVersionHigher(ver.tag, repo.version)) {
                            repo.versions.push(ver);
                            repo.version = ver.tag;
                        }
                    }
                }

                setRepoData(map);
            } catch (error) {
                if (!cancelled) console.error("Error fetching connector/version data:", error);
            }
        };

        fetchData();

        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <>
            <div>
                <div className="summary-card">
                    <label className="filter-label">
                        <FontAwesomeIcon icon={faMagnifyingGlass} /> {t("connectors.filterLabel")}
                    </label>
                    <input className="filter-input"
                        type="text"
                        value={nameFilter}
                        onChange={(e) => setNameFilter(e.target.value)}
                        placeholder={t("connectors.filterPlaceholder")}
                        onFocus={(e) => {
                            e.target.style.borderColor = "#4caf50";
                            e.target.style.boxShadow = "0 0 0 2px rgba(76, 175, 80, 0.3)";
                        }}
                        onBlur={(e) => {
                            e.target.style.borderColor = "#d1d5db";
                            e.target.style.boxShadow = "none";
                        }}
                    />
                </div>
            </div>

            <div className="preview-note">
                <FontAwesomeIcon icon={faCircleInfo} /> {t("connectors.previewNote")}
            </div>

            <div className="table-wrapper">
                <table>
                    <thead>
                    <tr>
                        <th></th>
                        <th>{t("connectors.table.connectorName")}</th>
                        <th>{t("connectors.table.latestVersion")}</th>
                        <th>{t("connectors.table.gitlabRepo")}</th>
                        <th>{t("connectors.table.releaseNotes")}</th>
                    </tr>
                    </thead>
                    <tbody>
                    {Object.values(repoData)
                        .filter(
                            (repo) =>
                                nameFilter === "" ||
                                repo.nameOfRepo.toLowerCase().includes(nameFilter.toLowerCase()) ||
                                repo.tableName.toLowerCase().includes(nameFilter.toLowerCase())
                        )
                        .sort((a, b) => a.tableName.localeCompare(b.tableName))
                        .map((repo) => {
                            const connectorVersionParam = encodeURIComponent(repo.nameOfRepo + "_" + repo.version);
                            const releaseNotesUrl = `/release-note?module-version=${connectorVersionParam}`;

                            const major = repo.version ? repo.version.split(".")[0] : "";
                            const releaseNotesConnectorUrl = `/connectordetail?repository=${encodeURIComponent(
                                repo.nameOfRepo
                            )}&major=${encodeURIComponent(major)}`;

                            return (
                                <tr key={repo.uniqueKey}>
                                    <td>
                                        <button className="icon-button"
                                            title={t("connectors.actions.viewVersions")}
                                        >
                                            <Link to={releaseNotesConnectorUrl}><FontAwesomeIcon icon={faMagnifyingGlass} /></Link>
                                        </button>
                                    </td>
                                    <td>{repo.tableName}</td>
                                    <td>
                                        {repo.version ? <Link to={releaseNotesUrl}>{repo.version}</Link> : ""}
                                    </td>
                                    <td>
                                        {repo.urlGithubGitlabRepo ? (
                                            <a href={repo.urlGithubGitlabRepo} target="_blank" rel="noreferrer">
                                                {t("connectors.actions.repo")}
                                            </a>
                                        ) : (
                                            ""
                                        )}
                                    </td>
                                    <td>
                                        <Link to={releaseNotesConnectorUrl}>{t("connectors.actions.notes")}</Link>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </>
    );
}

export default ConnectorsPage;